import { dev } from "@utils/dev";
import { todayUtc } from "@utils/moscowTime";
import { Moment } from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import ChartUtils from "./ChartUtils";
import { ChartPeriodDefaultValue, MomentPeriod, NullableChartPeriod } from "./CleanChart.control";
import { isChartPeriodsIsSame } from "./CleanChart.utils";

export interface IChartPeriodControl {
	readonly pickerValue: MomentPeriod | null;
	readonly period: NullableChartPeriod;
	readonly currentDay: Moment | null;
	updateDay(day: Moment, tag: string): void;
	updatePeriod(value: MomentPeriod): void;
	clearPeriod(): void;
	getPeriodDaysCount(): number;
	expandToLeft(days: number): void;
}

interface IChartPeriodControlProps {
	onPeriodChanged?: (period: NullableChartPeriod) => void;
}

export default function useChartPeriodControl({
	onPeriodChanged
}: IChartPeriodControlProps): IChartPeriodControl {

	const [pickerValue, setPickerValue] = useState<MomentPeriod | null>(null);
	const [period, setPeriod] = useState<NullableChartPeriod>(ChartPeriodDefaultValue); // real for server
	const [currentDay, setCurrentDay] = useState<Moment | null>(todayUtc());
	const prevPeriodRef = useRef<NullableChartPeriod>(ChartPeriodDefaultValue);

	function updateDay(day: Moment, tag: string) {
		//dev.logt("Charts", `updateDay ${tag}`, day);
		setPickerValue(null);
		setCurrentDay(day);
		const result = ChartUtils.createPeriodForRequest(day);
		setPeriod(result);
	}

	function updatePeriod(value: MomentPeriod) {
		setPickerValue(value);
		setCurrentDay(null);
		const result = ChartUtils.createPeriodForRequest(value[0] as Moment, value[1] as Moment);
		setPeriod(result);
	}

	function clearPeriod() {
		const day = todayUtc();
		setPickerValue(null);
		setCurrentDay(day);
		const result = ChartUtils.createPeriodForRequest(day);
		setPeriod(result);
	}

	const getPeriodDaysCount = useCallback((): number => {
		if (period[0] === null && period[1] === null) {
			return 1;
		}
		if (period[0] !== null && period[1] !== null) {
			return period[1].diff(period[0], "days");
		}
		return -1; // incorrect
	}, [period]);

	const expandToLeft = useCallback((days: number) => {
		let start: Moment | null = null;
		let end: Moment | null = null;
		if (period[0] === null && period[1] === null) {
			end = todayUtc().clone();
			start = end.clone().add(-days, "days");
		} else if (period[0] !== null && period[1] !== null) {
			end = period[1].clone().add(-1, "days");
			start = period[1].clone().add(-days, "days");
		}
		if (start !== null && end !== null) {
			setCurrentDay(null);
			setPickerValue([start, end]);
			const result = ChartUtils.createPeriodForRequest(start, end);
			setPeriod(result);
		}
	}, [period]);

	useEffect(() => {
		if (onPeriodChanged) {
			if (!isChartPeriodsIsSame(prevPeriodRef.current, period)) {
				onPeriodChanged(period);
			}
		}
		prevPeriodRef.current = period;
	}, [period])

	return {
		pickerValue,
		period,
		currentDay,
		expandToLeft,
		updateDay,
		updatePeriod,
		clearPeriod,
		getPeriodDaysCount,
	}
}